// src/features/admin/pages/AdminOrders.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { MotionConfig, AnimatePresence, motion } from 'framer-motion';
import {
  Receipt,
  RefreshCw,
  Search,
  Eye,
  X,
  MapPin,
  Mail,
  Phone,
  PackageSearch,
  AlertCircle,
} from 'lucide-react';
import { OrderService } from '../../../services/orders/orderService';

const statusStyles = {
  pending: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40',
  processing: 'bg-brand-red/20 text-brand-gold border border-brand-red/40',
  shipped: 'bg-blue-500/20 text-blue-300 border border-blue-500/40',
  delivered: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40',
  cancelled: 'bg-red-500/20 text-red-300 border border-red-500/40',
  default: 'bg-brand-black/60 text-brand-light border border-brand-red/30',
};

const paymentStyles = {
  pending: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40',
  processing: 'bg-brand-red/20 text-brand-gold border border-brand-red/40',
  completed: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40',
  failed: 'bg-red-500/20 text-red-300 border border-red-500/40',
  refunded: 'bg-blue-500/20 text-blue-300 border border-blue-500/40',
  default: 'bg-brand-black/60 text-brand-light border border-brand-red/30',
};

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(value);

const formatDate = (value) => {
  if (!value) return '--';
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await OrderService.getAllOrders(200);
      setOrders(data);
    } catch (err) {
      console.error('Error loading orders:', err);
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const filteredOrders = useMemo(() => {
    return orders
      .filter((order) => {
        if (statusFilter !== 'all' && order.status !== statusFilter) {
          return false;
        }
        if (paymentFilter !== 'all' && order.paymentStatus !== paymentFilter) {
          return false;
        }
        if (!searchTerm) return true;

        const term = searchTerm.toLowerCase();
        return (
          order.orderNumber?.toLowerCase().includes(term) ||
          `${order.customer?.firstName ?? ''} ${order.customer?.lastName ?? ''}`
            .toLowerCase()
            .includes(term) ||
          order.customer?.email?.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB - dateA;
      });
  }, [orders, statusFilter, paymentFilter, searchTerm]);

  const openDetails = (order) => setSelectedOrder(order);
  const closeDetails = () => setSelectedOrder(null);

  return (
    <div className="min-h-screen bg-brand-black text-brand-light">
      <div className="bg-brand-black/85 border-b border-brand-red/25 shadow-[0_20px_60px_-40px_rgba(230,0,0,0.45)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-heading font-semibold text-brand-gold flex items-center gap-3">
                <Receipt className="w-6 h-6 text-brand-red" />
                Orders
              </h1>
              <p className="text-sm text-brand-light/60 mt-1">
                Track customer purchases, payment states, and fulfillment progress.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setRefreshing(true);
                  loadOrders();
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-brand-red/30 bg-brand-black/70 text-brand-light hover:border-brand-red/50 transition-colors font-body"
              >
                <RefreshCw
                  className={`w-4 h-4 ${refreshing ? 'animate-spin text-brand-gold' : 'text-brand-gold'}`}
                />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {error && (
          <div className="bg-red-500/15 border border-red-500/40 text-red-200 rounded-xl p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {/* Filters */}
        <div className="bg-brand-black/80 border border-brand-red/25 rounded-2xl p-4 sm:p-6 shadow-[0_25px_60px_-35px_rgba(230,0,0,0.35)]">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-body font-medium text-brand-light/70 uppercase tracking-[0.3em] mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-light/40" />
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light placeholder:text-brand-light/40 focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all font-body text-sm"
                  placeholder="Search by order number, customer name, or email"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-body font-medium text-brand-light/70 uppercase tracking-[0.3em] mb-2">
                Order Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2.5 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all font-body text-sm"
              >
                <option value="all">All statuses</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-body font-medium text-brand-light/70 uppercase tracking-[0.3em] mb-2">
                Payment Status
              </label>
              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                className="w-full px-4 py-2.5 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all font-body text-sm"
              >
                <option value="all">All payments</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>
        </div>

        {/* Orders */}
        <div className="bg-brand-black/80 border border-brand-red/25 rounded-2xl overflow-hidden shadow-[0_30px_70px_-40px_rgba(230,0,0,0.45)]">
          {loading ? (
            <div className="py-20 flex flex-col items-center justify-center gap-4">
              <div className="w-12 h-12 border-4 border-brand-gold border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-brand-light/60 font-body">Loading orders...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center gap-3">
              <PackageSearch className="w-12 h-12 text-brand-light/30" />
              <p className="text-brand-light/70 font-body">
                No orders match your filters. Adjust filters or refresh the list.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm text-brand-light">
                <thead className="bg-brand-black border-b border-brand-red/20 text-xs uppercase tracking-wide text-brand-light/60">
                  <tr>
                    <th className="px-6 py-4">Order #</th>
                    <th className="px-6 py-4">Customer</th>
                    <th className="px-6 py-4">Total</th>
                    <th className="px-6 py-4">Payment</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Created</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-red/15">
                  {filteredOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-brand-black/60 transition-colors">
                      <td className="px-6 py-4 font-body font-medium text-brand-gold">
                        {order.orderNumber}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="font-body font-medium text-brand-light">
                            {order.customer?.firstName} {order.customer?.lastName}
                          </span>
                          <span className="text-xs text-brand-light/60">
                            {order.customer?.email}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-body font-medium text-brand-light">
                        {formatCurrency(order.total)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-body font-medium ${paymentStyles[order.paymentStatus] ?? paymentStyles.default}`}
                        >
                          {order.paymentStatus ?? 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-body font-medium ${statusStyles[order.status] ?? statusStyles.default}`}
                        >
                          {order.status ?? 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-brand-light/70">
                        {formatDate(order.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => openDetails(order)}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-brand-red/30 hover:border-brand-red/50 rounded-xl text-brand-light transition-colors"
                        >
                          <Eye className="w-4 h-4 text-brand-gold" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Order Detail Drawer */}
      <AnimatePresence>
        {selectedOrder && (
          <MotionConfig transition={{ type: 'spring', stiffness: 200, damping: 24 }}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-brand-black/80 backdrop-blur-lg z-50 flex items-center justify-center px-4 py-10"
              onClick={closeDetails}
            >
              <motion.div
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 40, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="max-w-3xl w-full bg-brand-black/90 border border-brand-red/30 rounded-3xl p-6 lg:p-8 shadow-[0_40px_90px_-40px_rgba(230,0,0,0.55)] space-y-6"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-heading font-semibold text-brand-gold">
                      Order {selectedOrder.orderNumber}
                    </h2>
                    <p className="text-sm text-brand-light/60 mt-1">
                      Placed on {formatDate(selectedOrder.createdAt)}
                    </p>
                  </div>
                  <button
                    onClick={closeDetails}
                    className="p-2 text-brand-light/60 hover:text-brand-gold hover:bg-brand-black/60 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-brand-black/70 border border-brand-red/25 rounded-2xl p-5 space-y-4">
                    <h3 className="text-sm font-body font-semibold text-brand-gold uppercase tracking-[0.3em]">
                      Customer
                    </h3>
                    <div className="space-y-3 text-sm text-brand-light/70">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-brand-gold" />
                        <span>{selectedOrder.customer?.email}</span>
                      </div>
                      {selectedOrder.customer?.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-brand-gold" />
                          <span>{selectedOrder.customer?.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-brand-gold" />
                        <span>
                          {selectedOrder.shippingAddress?.address}, {selectedOrder.shippingAddress?.city},{' '}
                          {selectedOrder.shippingAddress?.province} {selectedOrder.shippingAddress?.postalCode}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-brand-black/70 border border-brand-red/25 rounded-2xl p-5 space-y-4">
                    <h3 className="text-sm font-body font-semibold text-brand-gold uppercase tracking-[0.3em]">
                      Summary
                    </h3>
                    <div className="space-y-2 text-sm text-brand-light/70">
                      <div className="flex justify-between">
                        <span>Subtotal</span>
                        <span>{formatCurrency(selectedOrder.subtotal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Shipping</span>
                        <span>{formatCurrency(selectedOrder.shipping)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Tax</span>
                        <span>{formatCurrency(selectedOrder.tax)}</span>
                      </div>
                      <div className="border-t border-brand-red/20 pt-2 mt-2 flex justify-between text-base font-body font-semibold text-brand-gold">
                        <span>Total</span>
                        <span>{formatCurrency(selectedOrder.total)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-body font-medium ${paymentStyles[selectedOrder.paymentStatus] ?? paymentStyles.default}`}
                      >
                        Payment: {selectedOrder.paymentStatus}
                      </span>
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-body font-medium ${statusStyles[selectedOrder.status] ?? statusStyles.default}`}
                      >
                        Order: {selectedOrder.status}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-black/70 border border-brand-red/25 rounded-2xl p-5">
                  <h3 className="text-sm font-body font-semibold text-brand-gold uppercase tracking-[0.3em] mb-4">
                    Items
                  </h3>
                  <div className="space-y-4">
                    {selectedOrder.items?.map((item) => (
                      <div key={item.id} className="flex items-center gap-4">
                        {item.image && (
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-16 h-16 rounded-xl object-cover border border-brand-red/30"
                          />
                        )}
                        <div className="flex-1">
                          <p className="text-sm font-body font-medium text-brand-light">{item.name}</p>
                          <p className="text-xs text-brand-light/60">
                            {item.category} • {item.type}
                          </p>
                        </div>
                        <div className="text-right text-sm text-brand-light/70">
                          <p>Qty: {item.quantity}</p>
                          <p>{formatCurrency(item.subtotal)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </MotionConfig>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminOrders;

